"use client";

import { ProConsColumn, type ProConsItem } from "../_shared/pros-cons-column";

const DOES: ProConsItem[] = [
  {
    label: "Authenticates and routes",
    body: "A proxy authenticates the client, applies configured routing or access rules, and forwards the request from its own IP.",
  },
  {
    label: "Can cache, filter, or inspect",
    body: "Some proxies can also cache, inspect, filter, or transform traffic, but these capabilities are not universal across every proxy.",
  },
  {
    label: "Changes the visible exit IP",
    body: "It changes the network path and source IP visible to destinations for configured traffic.",
  },
  {
    label: "Distributes load",
    body: "It can spread thousands of requests across many IPs so large data-collection jobs run reliably.",
  },
];

const DOES_NOT: ProConsItem[] = [
  {
    label: "Guarantee anonymity",
    body: "A proxy does not, by itself, guarantee anonymity. Cookies, accounts, browser characteristics, and other application-level signals may still identify a session or user.",
  },
  {
    label: "Encrypt every application",
    body: "A proxy normally applies to a configured application or protocol — it doesn't encrypt every app on a device the way a VPN tunnel can.",
  },
  {
    label: "Remove a destination's access rules",
    body: "Services can apply limits using IPs, accounts, API keys, cookies, sessions, or request patterns. Changing an exit IP does not remove the destination's rules.",
  },
  {
    label: "Guarantee access to any site",
    body: "No proxy type guarantees access to every website. IP source affects reputation, location accuracy, cost, and latency, but providers should be evaluated against representative, permitted workloads.",
  },
];

export default function ProxyDoesAndDoesnt() {
  return (
    <section
      aria-labelledby="proxy-does-and-doesnt-heading"
      className="border-border py-section relative border-b"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 max-w-3xl">
          <p className="text-muted-foreground mb-3 text-xs font-semibold">Scope</p>
          <h2
            id="proxy-does-and-doesnt-heading"
            className="text-foreground mb-3 scroll-mt-40 text-3xl font-bold tracking-tight sm:text-4xl"
          >
            What a proxy does and does not do
          </h2>
          <p className="text-muted-foreground text-base leading-relaxed">
            A proxy is networking infrastructure, not a universal privacy or access guarantee.
            Here&apos;s where its responsibility actually starts and stops.
          </p>
        </div>

        <div className="grid gap-2 sm:gap-4 md:grid-cols-2">
          <ProConsColumn heading="What it does" items={DOES} positive />
          <ProConsColumn heading="What it doesn't do" items={DOES_NOT} positive={false} />
        </div>
      </div>
    </section>
  );
}
